(() => {
	if (customElements.get("shipping-calculator")) return;
	class ShippingCalculator extends HTMLElement {
		initialized = false;
		abortController = null;
		_pendingRaf = null;
		_onSubmit = null;
		_onKeydown = null;
		_uiBound = false;
		_requestToken = 0;
		_state = this._createDefaultState();
		_input = null;
		_button = null;
		_resultRoot = null;
		_addressRow = null;
		_addressText = null;
		_methodsList = null;
		_errorEl = null;
		_liveEl = null;

		_onVariantChanged = (evt) => {
			const variant = evt?.detail?.variant || null;
			this._currentVariant = variant;

			this.abortController?.abort();
			this.abortController = null;
			this._setLoading(false);

			this._state = this._createDefaultState();

			if (this._resultRoot) this._resultRoot.hidden = true;
			if (this._liveEl) this._liveEl.textContent = "";
			if (this._errorEl) {
				this._errorEl.textContent = "";
				this._errorEl.hidden = true;
			}
			if (this._addressRow) {
				this._setAddressRowVisibility(false);
				this._addressText.textContent = "";
			}
			if (this._errorEl) {
				this._errorEl.textContent = "";
				this._errorEl.hidden = true;
			}
			if (this._liveEl) this._liveEl.textContent = "";
			if (this._methodsList) this._methodsList.innerHTML = "";
			if (this._input) {
				this._input.value = "";
				this._input.classList.remove("is-invalid");
			}
			if (this._button) this._button.disabled = false;
			this._requestToken += 1;

			this._scheduleRender();
		};

		_createDefaultState() {
			return { step: "idle", cep: "", address: "", methods: [] };
		}

		_defaults = {
			dayMin: 14,
			dayMax: 21,
			freeShippingText: "Frete Grátis",
			labelText: "Entrega estimada",
			locale: "pt-BR",
		};

		connectedCallback() {
			this._opts = this._readOptions();
			if (!this._isDropshipping()) {
				this.style.display = "none";
				return;
			}
			if (this.initialized) return;
			this.initialized = true;

			document.addEventListener("variant:changed", this._onVariantChanged);
			this._unsubVariant = window.FoxThemeEvents?.subscribe?.(
				PUB_SUB_EVENTS.variantChange,
				({ data }) =>
					this._onVariantChanged({ detail: { variant: data?.variant } }),
			);

			this._scheduleRender();
		}

		disconnectedCallback() {
			document.removeEventListener("variant:changed", this._onVariantChanged);
			if (typeof this._unsubVariant === "function") this._unsubVariant();
			this._unsubVariant = null;
			this._unbindUI();
			if (this._pendingRaf) cancelAnimationFrame(this._pendingRaf);
			this._pendingRaf = null;
			this.abortController?.abort();
			this.abortController = null;
			this._requestToken += 1;
			this._state = this._createDefaultState();
			this.initialized = false;
		}

		_readOptions() {
			const data = this.dataset || {};
			const coerceInt = (value, fallback) => {
				const n = parseInt(value, 10);
				return Number.isFinite(n) ? n : fallback;
			};
			return {
				daysMin: coerceInt(data.daysMin, this._defaults.dayMin),
				daysMax: coerceInt(data.daysMax, this._defaults.dayMax),
				freeShippingText:
					data.freeShippingText || this._defaults.freeShippingText,
				labelText: data.labelText || this._defaults.labelText,
				locale: data.locale || this._defaults.locale,
			};
		}

		_isDropshipping() {
			return this.dataset.isDropshipping === "true";
		}

		_setAddressRowVisibility(visible) {
			if (this._addressRow) {
				if (visible) {
					this._addressRow.removeAttribute("hidden");
				} else {
					this._addressRow.setAttribute("hidden", "");
				}
			}
		}

		_scheduleRender() {
			if (this._pendingRaf) return;
			this._pendingRaf = requestAnimationFrame(() => {
				this._pendingRaf = null;
				this._render();
			});
		}

		_bindUI() {
			if (this._uiBound) return;
			this._input = this.querySelector(".shipping-calculator__cep");
			this._button = this.querySelector(".shipping-calculator__submit");
			if (!this._input || !this._button) {
				console.warn(
					"<shipping-calculator> needs .shipping-calculator__cep and .shipping-calculator__submit",
				);
				return;
			}

			this._onSubmit = (ev) => {
				ev.preventDefault();
				this._ensureResultUi();
				const raw = (this._input.value || "").replace(/\D/g, "");
				if (raw.length !== 8) {
					this._showError("CEP inválido. Use 8 dígitos (ex.: 01311000).");
					return;
				}

				this._hideError();
				this._state = {
					...this._state,
					step: "loading",
					cep: raw,
					address: "",
					methods: [],
				};

				this._resultRoot.hidden = true;
				if (this._methodsList) this._methodsList.innerHTML = "";
				if (this._addressRow) {
					this._setAddressRowVisibility(true);
					this._addressText.textContent = "";
				}

				const supportsAbort = typeof AbortController === "function";
				if (supportsAbort) this.abortController?.abort();
				const controller = supportsAbort ? new AbortController() : null;
				this.abortController = controller;
				const requestToken = ++this._requestToken;
				const signal = controller?.signal;

				this._setLoading(true);

				Promise.all([
					this._fetchAddress(raw, signal),
					this._simulateFetch(raw, signal),
				])
					.then(([addressText]) => {
						if (signal?.aborted || this._requestToken !== requestToken) return;

						this.abortController = null;
						this._state = {
							...this._state,
							step: "done",
							address: addressText || "",
							methods: this._buildShippingMethods(),
						};
						this._setLoading(false);
						this._renderResult();
					})
					.catch((error) => {
						if (signal?.aborted || this._requestToken !== requestToken) return;

						this.abortController = null;
						console.error("[shipping-calculator] calculate error:", error);
						this._state = {
							...this._state,
							step: "error",
						};
						this._setLoading(false);
						this._showError(
							"Não foi possível calcular o frete. Tente novamente.",
						);
					});
			};

			this._onKeydown = (e) => {
				if (e.key === "Enter") this._onSubmit(e);
			};

			this._button.addEventListener("click", this._onSubmit);
			this._input.addEventListener("keydown", this._onKeydown);
			this._uiBound = true;
		}

		_unbindUI() {
			this._button?.removeEventListener("click", this._onSubmit);
			this._input?.removeEventListener("keydown", this._onKeydown);
			this._uiBound = false;
		}

		_ensureResultUi() {
			if (this._resultRoot) return;

			// live + error
			this._liveEl = document.createElement("div");
			this._liveEl.className = "shipping-calculator__live";
			this._liveEl.setAttribute("aria-live", "polite");
			this._liveEl.setAttribute("aria-atomic", "true");

			this._errorEl = document.createElement("div");
			this._errorEl.className = "shipping-calculator__error";
			this._errorEl.setAttribute("role", "alert");
			this._errorEl.hidden = true;

			// address row (appears right under the input row)
			this._addressRow = document.createElement("div");
			this._addressRow.className =
				"shipping-calculator__address shipping-calculator__row shipping-calculator__row--address";

			const icon = document.createElement("span");
			icon.className = "shipping-calculator__address-icon"; // style with your green pin
			icon.setAttribute("aria-hidden", "true");
			icon.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
        <path fill-rule="nonzero"
          d="m 14.837018,10.825607 c 0.784807,-0.784807 1.178381,-1.7312599 1.178381,-2.83936 0,-1.1081006 -0.393574,-2.054554 -1.176039,-2.8393606 A 3.8701504,3.8701504 0 0 0 12,3.9708497 c -1.110442,0 -2.0568953,0.3912301 -2.8393601,1.1760367 A 3.8701504,3.8701504 0 0 0 7.9822591,7.986247 c 0,1.1081001 0.3935742,2.054553 1.1783808,2.83936 0.7824648,0.784807 1.7289181,1.176039 2.8393601,1.176039 1.1081,0 2.054553,-0.391232 2.83936,-1.176039 z m 5.196121,-2.83936 c 0,1.1385546 -0.17336,2.075639 -0.515397,2.808905 L 13.806226,22.937399 A 1.8905638,1.8905638 0 0 1 13.051874,23.75266 1.9749012,1.9749012 0 0 1 12.002342,24.050185 2.0568959,2.0568959 0 0 1 10.934068,23.75266 1.8202826,1.8202826 0 0 1 10.212514,22.937399 L 4.4845989,10.795152 C 4.140221,10.061886 3.9668609,9.1248016 3.9668609,7.9839053 c 0,-2.2162007 0.7848066,-4.1091073 2.3520768,-5.6763765 a 7.7379581,7.7379581 0 0 1 5.6834043,-2.35676384 c 2.2162,0 4.109106,0.7848066 5.678719,2.35441984 a 7.7379581,7.7379581 0 0 1 2.349734,5.6810622 z"
        />
      </svg>
    `;
			this._addressText = document.createElement("span");
			this._addressText.className = "shipping-calculator__address-text";
			this._addressRow.append(icon, this._addressText);

			// methods container + list
			this._resultRoot = document.createElement("div");
			this._resultRoot.className =
				"shipping-calculator__result shipping-calculator__row shipping-calculator__row--methods";
			this._resultRoot.hidden = true;

			this._methodsList = document.createElement("ul");
			this._methodsList.className = "shipping-calculator__methods";
			this._methodsList.setAttribute("role", "list");
			this._resultRoot.append(this._methodsList);

			// assemble
			const row = this.querySelector(".shipping-calculator__row") || this;
			row.insertAdjacentElement("afterend", this._addressRow);
			this._addressRow.insertAdjacentElement("afterend", this._errorEl);
			this._errorEl.insertAdjacentElement("afterend", this._liveEl);
			this._liveEl.insertAdjacentElement("afterend", this._resultRoot);

			// Set initial hidden state
			this._setAddressRowVisibility(false);
		}

		// ---------- render entry points ----------
		_render() {
			this._bindUI();
			this._ensureResultUi();

			this._resultRoot.hidden = true;
			if (this._methodsList) this._methodsList.innerHTML = "";
			if (this._addressRow) {
				this._setAddressRowVisibility(false);
				this._addressText.textContent = "";
			}
		}

		_renderResult() {
			this._ensureResultUi();

			const { locale, freeShippingText } = this._opts;
			const dateLocale = locale || "pt-BR";
			const dateFormatter = new Intl.DateTimeFormat(dateLocale, {
				day: "2-digit",
				month: "2-digit",
			});

			if (this._addressRow) {
				const addressText = this._state.address?.trim();
				this._addressText.textContent = addressText || "CEP localizado";
				this._setAddressRowVisibility(true);
			}
			if (this._errorEl) {
				this._errorEl.textContent = "";
				this._errorEl.hidden = true;
			}
			if (this._liveEl) this._liveEl.textContent = "";

			const methods =
				this._state.methods && this._state.methods.length
					? this._state.methods
					: this._buildShippingMethods();

			this._methodsList.innerHTML = "";
			if (!methods.length) {
				this._resultRoot.hidden = true;
				return;
			}

			const coerceDate = (value) => {
				if (value instanceof Date) return value;
				if (typeof value === "number" && Number.isFinite(value)) {
					const d = new Date();
					d.setDate(d.getDate() + value);
					return d;
				}
				if (typeof value === "string") {
					const parsed = new Date(value);
					return Number.isNaN(parsed.getTime()) ? null : parsed;
				}
				return null;
			};

			methods.forEach((method) => {
				const li = document.createElement("li");
				li.className = "shipping-calculator__method";

				const nameEl = document.createElement("div");
				nameEl.className = "shipping-calculator__method-name";
				nameEl.textContent = method.name || "Correios PAC";

				const etaEl = document.createElement("div");
				etaEl.className = "shipping-calculator__method-eta";
				const etaMinDate = coerceDate(method.etaMin);
				const etaMaxDate = coerceDate(method.etaMax);
				let etaText = "";
				if (etaMinDate && etaMaxDate) {
					const minStr = dateFormatter.format(etaMinDate);
					const maxStr = dateFormatter.format(etaMaxDate);
					etaText =
						etaMinDate.getTime() === etaMaxDate.getTime()
							? minStr
							: `${minStr} - ${maxStr}`;
				} else if (etaMinDate) {
					etaText = dateFormatter.format(etaMinDate);
				} else if (etaMaxDate) {
					etaText = dateFormatter.format(etaMaxDate);
				}
				etaEl.textContent = etaText;

				const priceEl = document.createElement("div");
				priceEl.className = "shipping-calculator__method-price";
				let priceText = freeShippingText || "Grátis";
				if (typeof method.price === "number" && Number.isFinite(method.price)) {
					priceText = this._formatCurrency(method.price, dateLocale);
				} else if (typeof method.price === "string" && method.price.trim()) {
					priceText =
						method.price.trim() === "0"
							? freeShippingText || "Grátis"
							: method.price;
				}
				if (
					method.price === 0 ||
					method.price === "0" ||
					method.price === null ||
					typeof method.price === "undefined"
				) {
					priceText = freeShippingText || "Grátis";
				}
				priceEl.textContent = priceText;

				const infoEl = document.createElement("div");
				infoEl.className = "shipping-calculator__method-info";
				infoEl.append(nameEl, etaEl);

				li.append(infoEl, priceEl);
				this._methodsList.append(li);
			});

			this._resultRoot.hidden = false;
			this._resultRoot.classList.add("is-visible");
		}

		_setLoading(isLoading) {
			this._button?.toggleAttribute("disabled", !!isLoading);
			this._input?.toggleAttribute("disabled", !!isLoading);
			this.classList.toggle("is-loading", !!isLoading);
			if (this._resultRoot)
				this._resultRoot.classList.toggle("loading", !!isLoading);
		}

		_simulateFetch(cep, signal) {
			const ms = 600 + Math.floor(Math.random() * 400);
			return new Promise((res, rej) => {
				const timeoutId = setTimeout(() => {
					if (signal?.aborted) return;
					res();
				}, ms);

				if (!signal) return;

				if (signal.aborted) {
					clearTimeout(timeoutId);
					const error = new Error("Aborted");
					error.name = "AbortError";
					rej(error);
					return;
				}

				signal.addEventListener(
					"abort",
					() => {
						clearTimeout(timeoutId);
						const error = new Error("Aborted");
						error.name = "AbortError";
						rej(error);
					},
					{ once: true },
				);
			});
		}

		_computeDates(minDays, maxDays) {
			const add = (n) => {
				const d = new Date();
				d.setDate(d.getDate() + n);
				return d;
			};
			return [add(minDays), add(maxDays)];
		}

		_buildShippingMethods() {
			const daysMin =
				typeof this._opts?.daysMin === "number"
					? this._opts.daysMin
					: this._defaults.dayMin;
			const daysMax =
				typeof this._opts?.daysMax === "number"
					? this._opts.daysMax
					: this._defaults.dayMax;
			const [etaMin, etaMax] = this._computeDates(daysMin, daysMax);
			return [
				{
					id: "correios PAC",
					name: "Correios PAC",
					etaMin,
					etaMax,
					price: 0,
				},
			];
		}

		_formatCurrency(value, locale = "pt-BR") {
			try {
				return new Intl.NumberFormat(locale || "pt-BR", {
					style: "currency",
					currency: "BRL",
				}).format(value);
			} catch {
				const asNumber = Number(value);
				if (Number.isFinite(asNumber)) {
					return `R$ ${asNumber.toFixed(2)}`;
				}
				return value?.toString?.() || "";
			}
		}

		async _fetchAddress(cep, signal) {
			try {
				const fetchOpts = signal ? { signal } : {};
				const res = await fetch(
					`https://viacep.com.br/ws/${cep}/json/`,
					fetchOpts,
				);
				if (!res.ok) throw new Error("CEP lookup failed");
				const data = await res.json();
				if (data.erro) return "CEP não encontrado";
				const rua = data.logradouro ? `${data.logradouro}, ` : "";
				const bairro = data.bairro ? `${data.bairro} - ` : "";
				return `${rua}${bairro}${data.localidade} - ${data.uf}`;
			} catch {
				return "Não foi possível obter o endereço";
			}
		}

		_showError(msg) {
			this._ensureResultUi();
			this._resultRoot.hidden = true;
			if (this._methodsList) this._methodsList.innerHTML = "";
			if (this._addressRow) {
				this._setAddressRowVisibility(false);
				this._addressText.textContent = "";
			}
			this._input?.classList.add("is-invalid");
			this._errorEl.textContent = msg;
			this._errorEl.hidden = false;
		}

		_hideError() {
			this._ensureResultUi();
			this._input?.classList.remove("is-invalid");
			this._errorEl.textContent = "";
			this._errorEl.hidden = true;
		}

		calculate(cep) {
			if (!this._uiBound) this._bindUI();
			if (!this._input || !this._button) return;
			this._input.value = (cep || "").toString();
			this._button.click();
		}
	}
	customElements.define("shipping-calculator", ShippingCalculator);
})();
